/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable react/prop-types */
import React, { useEffect, useRef } from 'react';
import './MenuNavigation.scss';
import { useLanguage } from '@components/common/locale/useLanguage';
import { _ } from '@evershop/evershop/src/lib/locale/translate';
// import { LoadingSpinner } from '@components/frontStore/catalog/product/single/LoadingSpinner';

var GET_ALL_CATEGORIES = "\n  query {\n    categories {\n      items {\n        name\n        nameAr\n        urlKey\n      }\n    }\n  }\n";
function MenuNavigation(_ref) {
  var {
    categories = [],
    activeCategory,
    onCategorySelect
  } = _ref;
  var {
    language
  } = useLanguage();
  var [loading, setLoading] = React.useState(categories.length === 0);
  var [error, setError] = React.useState(null);
  var [localCategories, setLocalCategories] = React.useState(categories);
  var scrollContainerRef = useRef(null);

  // Add ref to track if we're manually scrolling
  var isManualScrolling = useRef(false);
  var observerRef = useRef(null);

  // Add this effect to set up intersection observers for each category section
  useEffect(() => {
    if (localCategories.length === 0 || !onCategorySelect) return;

    // Create an observer to detect when a category section is visible
    var observerOptions = {
      root: null,
      // viewport
      rootMargin: '-10% 0px -80% 0px',
      threshold: 0 // Trigger when any part of the element is visible
    };
    var observerCallback = entries => {
      // Don't update during manual scrolling
      if (isManualScrolling.current) return;
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          // Extract category name from the element ID
          var categoryId = entry.target.id;
          var categoryName = categoryId.replace('category-', '').split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');

          // Find the matching category in our list
          var matchedCategory = localCategories.find(cat => cat.name.toLowerCase() === categoryName.toLowerCase());
          if (matchedCategory) {
            onCategorySelect(matchedCategory.name);
          }
        }
      });
    };
    var observer = new IntersectionObserver(observerCallback, observerOptions);
    observerRef.current = observer;

    // Observe all category sections
    localCategories.forEach(category => {
      var categoryId = "category-".concat(category.name.toLowerCase().replace(/\s+/g, '-'));
      var element = document.getElementById(categoryId);
      if (element) {
        observer.observe(element);
      }
    });
    // Clean up
    // eslint-disable-next-line consistent-return
    return () => {
      observer.disconnect();
    };
  }, [localCategories, onCategorySelect]);
  React.useEffect(() => {
    // Only fetch categories if none were passed as props
    if (categories.length === 0) {
      var fetchCategories = async () => {
        try {
          var response = await fetch('/api/graphql', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              query: GET_ALL_CATEGORIES
            })
          });
          var result = await response.json();
          if (result.errors) {
            throw new Error(result.errors[0].message);
          }

          // Filter out categories that contain "2nd Side" or "1st Side"
          var filteredCategories = result.data.categories.items.filter(category => !/^(2nd Side|1st Side|Mini)$/i.test(category.name));

          // Define the desired order of categories
          var categoryOrder = ['Appetizers', 'Salads', 'Pasta', 'Asian', 'Rice Dishes', 'Oriental Meals', 'Main Dishes', 'Sandwiches', 'Pizzas', 'Desserts', 'Side items', 'Sauces', 'Combo', 'Soft Drinks'];

          // Sort the categories based on the defined order
          var sortedCategories = [...filteredCategories].sort((a, b) => {
            var indexA = categoryOrder.indexOf(a.name);
            var indexB = categoryOrder.indexOf(b.name);

            // If a category is not in the list, put it at the end
            if (indexA === -1) return 1;
            if (indexB === -1) return -1;
            return indexA - indexB;
          });
          setLocalCategories(sortedCategories);
          setLoading(false);
        } catch (err) {
          setError(err.message);
          setLoading(false);
        }
      };
      fetchCategories();
    } else {
      setLocalCategories(categories);
      setLoading(false);
    }
  }, [categories]);

  // Effect to scroll active category into view in the navigation
  useEffect(() => {
    if (activeCategory && scrollContainerRef.current) {
      // Find the active element by name attribute (case-insensitive)
      var activeElements = Array.from(scrollContainerRef.current.querySelectorAll('[data-category]'));
      var activeElement = activeElements.find(el => {
        var categoryName = el.getAttribute('data-category');
        return categoryName && categoryName.toLowerCase() === activeCategory.toLowerCase();
      });
      if (activeElement) {
        // Calculate position to scroll to
        var containerWidth = scrollContainerRef.current.offsetWidth;
        var elementLeft = activeElement.offsetLeft;
        var elementWidth = activeElement.offsetWidth;

        // Center the element in the scrollable area
        var scrollToPosition = elementLeft - containerWidth / 2 + elementWidth / 2;
        scrollContainerRef.current.scrollTo({
          left: scrollToPosition,
          behavior: 'smooth'
        });
      }
    }
  }, [activeCategory, categories]);

  // Function to scroll to a specific category section
  var scrollToCategory = (categoryName, e) => {
    e.preventDefault();

    // Set manual scrolling flag to true
    isManualScrolling.current = true;

    // Update active category immediately for a smoother UI experience
    if (onCategorySelect) {
      onCategorySelect(categoryName);
    }

    // Create a category ID that matches the one in the Menu component
    var categoryId = "category-".concat(categoryName.toLowerCase().replace(/\s+/g, '-'));
    var element = document.getElementById(categoryId);
    if (element) {
      // The scroll is now handled by the onCategorySelect -> scrollToDesktopCategory/scrollToMobileCategory
      // which uses container-specific scrollTo logic, avoiding window scroll issues.

      // Reset the manual scrolling flag after the scroll animation completes
      setTimeout(() => {
        isManualScrolling.current = false;
      }, 1000); // Adjust this timeout to match your scroll animation duration
    } else {
      // If no element found, reset the flag immediately
      isManualScrolling.current = false;
    }
  };
  var chunkCategories = cats => {
    var chunks = [];
    var i = 0;
    var pattern = [1, 3, 3];
    var patternIndex = 0;
    while (i < cats.length) {
      var chunkSize = void 0;
      if (patternIndex < pattern.length) {
        chunkSize = pattern[patternIndex];
        patternIndex += 1;
      } else {
        chunkSize = 4;
      }
      chunks.push(cats.slice(i, i + chunkSize));
      i += chunkSize;
    }
    return chunks;
  };
  if (loading) return /*#__PURE__*/React.createElement("div", {
    className: "loading"
  }, "Loading");
  if (error) return /*#__PURE__*/React.createElement("div", {
    className: "error"
  }, "Error loading categories: ", error);
  var categoryChunks = chunkCategories(localCategories);
  return /*#__PURE__*/React.createElement("div", {
    className: "flex flex-col md:flex-row gap-8 md:gap-[44rem] items-start justify-between max-w-[108rem] mx-auto mt-8 px-4 ltr:ml-auto rtl:mr-auto"
  }, /*#__PURE__*/React.createElement("div", {
    className: "flex-shrink-0"
  }, /*#__PURE__*/React.createElement("h2", {
    className: "menu-title uppercase font-zoom-pro-compact font-[700]"
  }, _('Menu'))), /*#__PURE__*/React.createElement("div", {
    className: ""
  }, localCategories.length === 0 ? /*#__PURE__*/React.createElement("p", null, "No categories found") : /*#__PURE__*/React.createElement("div", {
    className: "category-columns-wrapper flex flex-wrap gap-x-[5.2rem]"
  }, categoryChunks.map((chunk, columnIndex) => /*#__PURE__*/React.createElement("ul", {
    key: "column-".concat(columnIndex),
    className: "category-column flex flex-col gap-y-2"
  }, chunk.map(category => {
    var isActive = activeCategory === category.name;
    return /*#__PURE__*/React.createElement("li", {
      key: category.urlKey,
      className: "category-item"
    }, /*#__PURE__*/React.createElement("a", {
      className: "text-[1.4rem] md:text-[1.6rem] transition-all duration-300 cursor-pointer uppercase ".concat(isActive ? 'text-[#000] decoration-2 font-zoom-pro-compact font-[500] underline-offset-4' : 'text-[#7B7B7B] font-zoom-pro-compact font-[400] hover:text-[#000]'),
      href: "#category-".concat(category.name.toLowerCase().replace(/\s+/g, '-')),
      onClick: e => scrollToCategory(category.name, e)
    }, language === 'ar' ? category.nameAr || category.name : category.name));
  }))))));
}
export default MenuNavigation;